import { useEffect, useState } from "react";
import api from "@/lib/api";
import { openAuthedFile, isImage } from "@/lib/files";
import PasteZone from "@/components/PasteZone";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  FileText, Image as ImageIcon, Upload, Trash2, Eye, Loader2, AlertTriangle,
  CheckCircle2, Plus,
} from "lucide-react";

const isExpired = (d) => d && new Date(d) < new Date(new Date().toDateString());

// Reusable documents manager for pilots / aircraft.
// docTypes: [{ key, label }]. Each doc = one file + a validity date.
export default function EntityDocuments({ entityType, entityId, docTypes }) {
  const [docs, setDocs] = useState([]);
  const [pendingDate, setPendingDate] = useState({});
  const [busy, setBusy] = useState("");
  const [customs, setCustoms] = useState([]);
  const [newLabel, setNewLabel] = useState("");

  const load = async () => {
    try {
      setDocs((await api.get(`/${entityType}/${entityId}/documents`)).data);
    } catch {
      setDocs([]);
    }
  };
  useEffect(() => { if (entityId) load(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  const findDoc = (key) => docs.find((d) => d.doc_type === key);

  const upload = async (key, label, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", key);
    fd.append("label", label || "");
    fd.append("validity_date", pendingDate[key] || "");
    setBusy(key);
    try {
      await api.post(`/${entityType}/${entityId}/documents`, fd);
      toast.success("Document enregistré");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'upload");
    } finally {
      setBusy("");
      e.target.value = "";
    }
  };

  const setValidity = async (doc, date) => {
    setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, validity_date: date } : d)));
    try {
      await api.put(`/${entityType}/${entityId}/documents/${doc.id}`, { validity_date: date });
    } catch {
      toast.error("Impossible de mettre à jour la date");
      await load();
    }
  };

  const remove = async (doc) => {
    try {
      await api.delete(`/${entityType}/${entityId}/documents/${doc.id}`);
      await load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const uploadCapture = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", `capture_${Date.now()}`);
    fd.append("label", "Capture d'écran");
    fd.append("validity_date", "");
    setBusy("__paste__");
    try {
      await api.post(`/${entityType}/${entityId}/documents`, fd);
      toast.success("Capture enregistrée");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'upload");
    } finally {
      setBusy("");
    }
  };

  const addCustom = () => {
    const label = newLabel.trim();
    if (!label) return;
    setCustoms((c) => [...c, { key: `custom_${Date.now()}`, label }]);
    setNewLabel("");
  };

  if (!entityId) {
    return <p className="text-sm text-slate-500">Enregistrez d'abord la fiche pour joindre des documents.</p>;
  }

  const knownKeys = new Set([...docTypes, ...customs].map((t) => t.key));
  const orphanDocs = docs.filter((d) => !knownKeys.has(d.doc_type));
  const allTypes = [
    ...docTypes,
    ...customs,
    ...orphanDocs.map((d) => ({ key: d.doc_type, label: d.label || d.doc_type })),
  ];

  return (
    <div className="space-y-3">
      {allTypes.map((t) => {
        const doc = findDoc(t.key);
        const expired = doc && isExpired(doc.validity_date);
        return (
          <div key={t.key} data-testid={`doc-row-${t.key}`}
            className="flex flex-col gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-center gap-2">
              {doc ? (isImage(doc.content_type) ? <ImageIcon className="h-4 w-4 text-cyan-500/70" /> : <FileText className="h-4 w-4 text-cyan-500/70" />)
                : <FileText className="h-4 w-4 text-slate-600" />}
              <div>
                <div className="text-sm font-medium text-slate-200">{t.label}</div>
                {doc && <div className="truncate text-[11px] text-slate-500">{doc.original_filename}</div>}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Validité</div>
                <Input type="date"
                  value={doc ? (doc.validity_date || "") : (pendingDate[t.key] || "")}
                  onChange={(e) => doc ? setValidity(doc, e.target.value) : setPendingDate((p) => ({ ...p, [t.key]: e.target.value }))}
                  data-testid={`doc-date-${t.key}`}
                  className={`h-9 w-40 border-slate-700 bg-slate-900/60 font-mono-data text-sm ${expired ? "text-rose-300" : "text-slate-100"}`} />
              </div>

              {expired && (
                <span className="flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-1 text-[10px] font-semibold text-rose-300">
                  <AlertTriangle className="h-3 w-3" /> Expiré
                </span>
              )}
              {doc && !expired && doc.validity_date && (
                <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold text-emerald-300">
                  <CheckCircle2 className="h-3 w-3" /> Valide
                </span>
              )}

              {doc ? (
                <>
                  <button onClick={() => openAuthedFile(`/${entityType}/${entityId}/documents/${doc.id}/download`)}
                    className="rounded p-2 text-slate-400 hover:bg-slate-800 hover:text-cyan-300" title="Voir" data-testid={`doc-view-${t.key}`}>
                    <Eye className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(doc)}
                    className="rounded p-2 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300" title="Supprimer" data-testid={`doc-delete-${t.key}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs font-medium text-cyan-300 hover:bg-cyan-500/20">
                  {busy === t.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Téléverser
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,image/*,application/pdf" className="hidden"
                    onChange={(e) => upload(t.key, t.label, e)} disabled={busy === t.key} data-testid={`doc-upload-${t.key}`} />
                </label>
              )}
            </div>
          </div>
        );
      })}

      <PasteZone onImage={uploadCapture} busy={busy === "__paste__"} />

      <div className="flex items-center gap-2 pt-1">
        <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Autre document (intitulé)"
          onKeyDown={(e) => e.key === "Enter" && addCustom()} data-testid="doc-custom-label"
          className="h-9 border-slate-700 bg-slate-900/60 text-sm text-slate-100" />
        <Button onClick={addCustom} variant="outline" size="sm" className="border-slate-700 bg-transparent text-cyan-300 hover:bg-cyan-500/10">
          <Plus className="mr-1 h-3.5 w-3.5" /> Ajouter
        </Button>
      </div>
    </div>
  );
}
